import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import {
    CreateWorkbookReminderPayload,
    GetWorkbookReminderPayload,
    UpdateWorkbookReminderPayload,
    WorkbookReminder,
    WorkbookReminderInputProps, TimeOption,
} from '../models/workbook-reminder.models';
import { WorkbookReminderService } from '../services/workbook-reminder.service';
import { AppMainService } from 'src/app/app-main.service';
import { ConfirmationService, MessageService } from 'primeng/api';
import { debounceTime, Subject, takeUntil } from 'rxjs';
import { ReminderFilterOption, ReminderStatus } from '../enum/workbook-reminder.enum';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { TimeZoneService } from 'src/app/modules/shared/services/timezone.service';
import { AppMainComponent } from 'src/app/app.main.component';
import { AccountModuleId, CaseModuleId, LeadModuleId, OpportunityModuleId } from 'src/app/modules/shared/constants/modules.constant';

@Component({
    selector: 'app-workbook-reminder',
    templateUrl: './workbook-reminder.component.html',
    styleUrl: './workbook-reminder.component.scss',
})
export class WorkbookReminderComponent implements OnInit, OnDestroy {
    /** Subscription and Cleanup */
    protected ngUnsubscribe$: Subject<void> = new Subject<void>();

    @Input() displayReminder: boolean = false;
    @Input() workbookReminderProps: WorkbookReminderInputProps;
    @Output() onHide = new EventEmitter<void>();
    @Output() reminderUpdateStatus? = new EventEmitter<{ isUpdate: boolean }>();

    reminderStatus = ReminderStatus;
    reminders: WorkbookReminder[] = [];
    headerName = 'Reminder';
    isAddMode = false;
    filterOption: ReminderFilterOption = ReminderFilterOption.Future;
    // Dropdown options
    filterOptions = [
        { label: 'All', value: ReminderFilterOption.All },
        { label: 'Past', value: ReminderFilterOption.Past },
        { label: 'Upcoming', value: ReminderFilterOption.Future },
    ];
    reminderForm: FormGroup;
    isEditMode: boolean = false; // Determines if it's add or edit mode
    selectedReminder: WorkbookReminder | null = null; // Holds the ID of the reminder being edited

    /** pagination*/
    rowsPerPage: number = 2;
    first: number = 0;
    paginatedReminders: WorkbookReminder[] = []; // List for the current page to display

    /**reminder date time */
    hourOptions: TimeOption[] = [];
    minuteOptions: TimeOption[] = [];
    selectedDate: Date;
    selectedHour: number;
    selectedMinute: number;

    periodOptions = [
        { label: 'AM', value: 'AM' },
        { label: 'PM', value: 'PM' }
    ];
    minDate = new Date();

    constructor(
        private readonly workbookReminderService: WorkbookReminderService,
        private readonly appMainService: AppMainService,
        private readonly confirmationService: ConfirmationService,
        private readonly messageService: MessageService,
        private fb: FormBuilder,
        private readonly timezoneService: TimeZoneService,
        private readonly app: AppMainComponent,
    ) {
        this.generateTimeOptions();
    }

    ngOnInit() {
        this.minDate = this.app._showDateTimeWithZone(new Date())
        this.getWorkbookReminder();
        let headerName = 'Reminder';
        if (this.workbookReminderProps?.rowIndex) {
            headerName = `Reminder: Row ${this.workbookReminderProps.rowIndex}`;
        } else if (this.workbookReminderProps?.moduleData?.businessName) {
            headerName = this.workbookReminderProps.moduleData?.sequenceNumber
                ? `Reminder (${this.workbookReminderProps.moduleData?.sequenceNumber}: ${this.workbookReminderProps.moduleData?.businessName})`
                : `Reminder (${this.workbookReminderProps.moduleData?.businessName})`;
        }
        else if (this.workbookReminderProps?.objectId === OpportunityModuleId) {
            headerName = this.workbookReminderProps.moduleData?.sequenceNumber
                ? `Reminder (${this.workbookReminderProps.moduleData?.sequenceNumber}: ${this.workbookReminderProps.moduleData?.name})`
                : `Reminder (${this.workbookReminderProps.moduleData?.name})`;
        }
        else if (this.workbookReminderProps?.objectId === CaseModuleId) {
            headerName = this.workbookReminderProps.moduleData?.sequenceNumber
                ? `Reminder (${this.workbookReminderProps.moduleData.sequenceNumber}: ${this.workbookReminderProps.moduleData?.associationName})`
                : `Reminder (${this.workbookReminderProps.moduleData.caseAssociationName} - ${this.workbookReminderProps.moduleData.associationName})`;
        }
        this.headerName = headerName;
    }

    // Modify initializeForm() to be more efficient
    initializeForm(): void {
        const defaultDateTime = this.getDefaultDateTime();
        const defaultHour = defaultDateTime.getHours();
        const isPM = defaultHour >= 12 ? 'PM' : 'AM';
        const hour12 = defaultHour > 12 ? defaultHour - 12 : (defaultHour === 0 ? 12 : defaultHour);

        this.reminderForm = this.fb.group({
            date: [defaultDateTime, [Validators.required]],
            period: [isPM, [Validators.required]], // Changed from periodSwitch to period
            hour: [hour12, [Validators.required]],
            minute: [0, [Validators.required, Validators.min(0), Validators.max(59)]],
            message: ['', [Validators.maxLength(500), this.noInitialSpaceValidator]]
        }, {
            validators: [this.futureDateValidator.bind(this)]
        });

        this.reminderForm.valueChanges
            .pipe(
                debounceTime(300),
                takeUntil(this.ngUnsubscribe$)
            )
            .subscribe(() => {
                this.validateDateTime();
            });
    }

    onPeriodChange(event: any): void {
        this.validateDateTime();
    }

    validateDateTime(): void {
        const formValue = this.reminderForm.value;
        if (!formValue.date || formValue.hour === null || formValue.minute === null) {
            return;
        }

        const errors = this.futureDateValidator(this.reminderForm);
        if (errors) {
            this.reminderForm.setErrors(errors);
        } else {
            // Only clear the pastDate error, preserve other potential errors
            const currentErrors = this.reminderForm.errors;
            if (currentErrors) {
                const { pastDate, ...otherErrors } = currentErrors;
                this.reminderForm.setErrors(Object.keys(otherErrors).length ? otherErrors : null);
            }
        }
    }

    futureDateValidator(control: AbstractControl): ValidationErrors | null {
        if (!control) return null;
        const dateControl = control.get('date');
        const hourControl = control.get('hour');
        const minuteControl = control.get('minute');
        const periodControl = control.get('period');
        if (!dateControl?.value || hourControl?.value === null ||
            minuteControl?.value === null || periodControl?.value === undefined) {
            return null;
        }
        const selectedDate = new Date(dateControl.value);
        let hour24 = hourControl.value;
        // Convert to 24-hour format
        if (periodControl.value=='PM' && hour24 !== 12) { // PM
            hour24 += 12;
        } else if (periodControl.value=='AM' && hour24 === 12) { // AM
            hour24 = 0;
        }
        selectedDate.setHours(hour24, minuteControl.value, 0, 0);
        const now = this.app._showDateTimeWithZone(new Date());

        const minimumValidTime = new Date(now.getTime() + (2 * 60 * 1000));

        return selectedDate.getTime() <= minimumValidTime.getTime() ? { pastDate: true } : null;
    }

    // Custom validator to ensure no leading spaces
    noInitialSpaceValidator(control: any) {
        if (
            control.value &&
            control.value.trimStart().length !== control.value.length
        ) {
            return { initialSpace: true };
        }
        return null;
    }

    onFilterChange(newFilterOption: ReminderFilterOption): void {
        this.filterOption = newFilterOption;
        this.getWorkbookReminder();
    }

    saveOrUpdateReminder(): void {
        if (this.reminderForm.valid) {
            const formValue = this.reminderForm.value;
            const localDate = new Date(formValue.date);
            let hour = formValue.hour;
            // Adjust hour for AM/PM
            if (formValue.period === 'PM' && hour !== 12) {
                hour += 12;  // Convert PM hour to 24-hour format
            } else if (formValue.period === 'AM' && hour === 12) {
                hour = 0;  // 12 AM is midnight
            }
            const selectedDate = new Date(
                localDate.getFullYear(),
                localDate.getMonth(),
                localDate.getDate(),
                hour,          // Adjusted hour
                formValue.minute
            );

            if (this.isEditMode) {
                const updatedReminder: UpdateWorkbookReminderPayload = {
                    id: this.selectedReminder.id,
                    date: this.timezoneService.saveDateTimeWithZone(selectedDate).toISOString(),
                    message: formValue.message,
                    statusId: ReminderStatus.Pending,
                };
                this.editWorkbookReminder(updatedReminder);
            } else {
                const newReminder: CreateWorkbookReminderPayload = {
                    objectId: this.workbookReminderProps.objectId,
                    objectRecordId: this.workbookReminderProps.objectRecordId,
                    date: this.timezoneService.saveDateTimeWithZone(selectedDate).toISOString(),
                    message: formValue.message,
                    ...(this.workbookReminderProps?.channelId && {
                        channelId: this.workbookReminderProps?.channelId
                    })
                };
                this.createWorkbookReminder(newReminder);
            }
        } else {
            this.reminderForm.markAllAsTouched();
        }
    }

    /** API Calls */
    createWorkbookReminder(newReminder: CreateWorkbookReminderPayload) {
        this.appMainService.isShowSpinner('addNewWorkbookReminder');
        this.workbookReminderService
            .addNewWorkbookReminder(newReminder)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: (response: any) => {
                    this.appMainService.isHideSpinner('addNewWorkbookReminder');
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'Reminder created successfully!',
                    });
                    this.reminderForm.reset();
                    this.isAddMode = false;
                    this.getWorkbookReminder();
                    const isUpdate = true;
                    this.reminderUpdateStatus.emit({ isUpdate });

                },
                error: (error) => {
                    this.appMainService.isHideSpinner('addNewWorkbookReminder');
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: error?.error?.Message || 'Failed to create reminder!',
                    });
                },
            });
    }

    getWorkbookReminder() {
        const crmMoudleList = [AccountModuleId, LeadModuleId, OpportunityModuleId, CaseModuleId]
        const reminder: GetWorkbookReminderPayload =
        crmMoudleList.includes(this.workbookReminderProps.objectId)
                ? {
                    workbookId: null,
                    rowNumber: this.workbookReminderProps.objectRecordId,
                    filterOption: this.filterOption,
                    objectId: this.workbookReminderProps.objectId
                }
                : {
                    workbookId: this.workbookReminderProps.channelId,
                    rowNumber: this.workbookReminderProps.objectRecordId,
                    filterOption: this.filterOption
                };

        this.appMainService.isShowSpinner('getWorkbookReminder');
        this.workbookReminderService
            .getWorkbookReminder(reminder)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: (response: WorkbookReminder[]) => {
                    // Process each reminder to adjust for timezone
                    this.reminders = response||[];
                    this.initializeForm();
                    this.appMainService.isHideSpinner('getWorkbookReminder');
                },
                error: (error) => {
                    this.reminders = [];
                    this.appMainService.isHideSpinner('getWorkbookReminder');
                },
            });
    }

    editWorkbookReminder(updatedReminder: UpdateWorkbookReminderPayload) {
        this.appMainService.isShowSpinner('updateWorkbookReminder');
        this.workbookReminderService
            .updateWorkbookReminder(updatedReminder)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: (response: any) => {
                    this.appMainService.isHideSpinner('updateWorkbookReminder');
                    this.resetForm();
                    this.getWorkbookReminder();
                    if (updatedReminder.statusId === ReminderStatus.Completed) {
                        this.messageService.add({
                            severity: 'success',
                            summary: 'Reminder Completed',
                            detail: 'The reminder has been marked as completed successfully!',
                        });
                    } else if (updatedReminder.statusId === ReminderStatus.Ignored) {
                        this.messageService.add({
                            severity: 'success',
                            summary: 'Reminder Ignored',
                            detail: 'The reminder has been ignored successfully!',
                        });
                    } else {
                        this.messageService.add({
                            severity: 'info',
                            summary: 'Reminder Updated',
                            detail: 'The reminder has been updated successfully!',
                        });
                    }
                },
                error: (error) => {
                    this.appMainService.isHideSpinner('updateWorkbookReminder');
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: error?.error?.Message || 'Failed to updated reminder!',
                    });
                },
            });
    }

    deleteWorkbookReminder(reminder) {
        this.confirmationService.confirm({
            message: 'Are you sure that you want to delete this reminder?',
            header: 'Delete Confirmation',
            icon: 'pi pi-info-circle',
            accept: () => {
                this.appMainService.isShowSpinner('deleteWorkbookReminder');
                this.workbookReminderService
                    .deleteWorkbookReminder(reminder.id)
                    .pipe(takeUntil(this.ngUnsubscribe$))
                    .subscribe({
                        next: (response: any) => {
                            this.appMainService.isHideSpinner('deleteWorkbookReminder');
                            this.messageService.add({
                                severity: 'success',
                                summary: 'Success',
                                detail: 'Reminder deleted successfully!',
                            });
                            this.getWorkbookReminder();
                        },
                        error: (error) => {
                            this.appMainService.isHideSpinner('deleteWorkbookReminder');
                            this.messageService.add({
                                severity: 'error',
                                summary: 'Error',
                                detail: error?.error?.Message || 'Failed to delete reminder!',
                            });
                        },
                    });
            },
        });
    }

    // Load reminder data into the form for editing

    openEditReminder(reminder: WorkbookReminder): void {
        if (!this.reminderForm) {
            this.initializeForm();
        }
        this.resetForm();
        this.isAddMode = false;
        this.isEditMode = true;
        this.selectedReminder = reminder;

        setTimeout(() => {
            const reminderDate = this.app._showDateTimeWithZone(reminder.date);
            let hours = reminderDate.getHours();
            let period = 'AM';

            // Convert 24-hour format to 12-hour format
            if (hours >= 12) {
                period = 'PM';
                if (hours > 12) {
                    hours -= 12; // Convert PM hours (e.g., 13 becomes 1, 14 becomes 2, etc.)
                }
            } else if (hours === 0) {
                hours = 12; // 0 hours (midnight) becomes 12 AM
            }

            this.reminderForm.patchValue({
                date: reminderDate,
                period: period,
                hour: hours,
                minute: reminderDate.getMinutes(),
                message: reminder.message || ''
            }, { emitEvent: false });
        });
    }

    /** Pagination methods */
    onWorkspacePageChange(event: any): void {
        // Handle page changes and update paginated workspace list
        this.first = event.first;
        this.rowsPerPage = event.rows;
        this.updatePaginatedReminders();
    }

    updatePaginatedReminders(): void {
        // Paginate the filtered workspace list
        const startIndex = this.first;
        const endIndex = startIndex + this.rowsPerPage;
        this.paginatedReminders = this.reminders.slice(startIndex, endIndex);
    }

    // Reset the form to its initial state
    resetForm(): void {
        this.isEditMode = false;
        this.selectedReminder = null;
        const defaultDateTime = this.getDefaultDateTime();
        this.reminderForm.reset({
            date: this.app._showDateTimeWithZone(defaultDateTime),
            hour: 8,
            minute: '00',
            message: '',
        });
    }

    closeRemiderModal() {
        this.isAddMode = false;
        this.onHide.emit();
    }

    updateWorkbookReminderStatus(reminder: WorkbookReminder, status: ReminderStatus) {
        const updatedReminderStatus: UpdateWorkbookReminderPayload = {
            id: reminder.id,
            statusId: status,
            date: reminder.date,
            message: reminder.message,
        }
        this.editWorkbookReminder(updatedReminderStatus);
    }

    addNewReminder() {
        this.isEditMode = false;
        this.initializeForm();
        this.isAddMode = true;
    }

    closeAddReminder() {
        this.isAddMode = false;
        this.isEditMode = false;
        this.reminderForm.reset();
    }

    private generateTimeOptions() {
        // Generate once and memoize
        if (!this.hourOptions.length) {
            this.hourOptions = Array.from({ length: 12 }, (_, i) => ({
                label: (i + 1).toString().padStart(2, '0'),
                value: i + 1
            }));
        }

        if (!this.minuteOptions.length) {
            this.minuteOptions = [0, 15, 30, 45].map(m => ({
                label: m.toString().padStart(2, '0'),
                value: m
            }));
        }
    }

    private getDefaultDateTime(): Date {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        tomorrow.setHours(8, 0, 0); // Set to 8:00:00 AM
        return tomorrow; // Return local time directly
    }

    validateMinuteInput(event: KeyboardEvent): boolean {
        const pattern = /^[0-9]$/;
        const inputChar = String.fromCharCode(event.charCode);
        if (!pattern.test(inputChar)) {
            event.preventDefault();
            return false;
        }

        const currentValue = (event.target as HTMLInputElement).value || '';
        const newValue = currentValue + inputChar;

        if (parseInt(newValue, 10) > 59) {
            event.preventDefault();
            return false;
        }

        return true;
    }

    onMinuteInput(event: any): void {
        const inputValue = (event.target?.value || '').replace(/[^0-9]/g, '').substring(0, 2);
        const numValue = Math.min(Math.max(parseInt(inputValue || '0', 10), 0), 59);

        this.reminderForm.patchValue({ minute: numValue }, { emitEvent: false });
        this.addMinuteOptionIfNeeded(numValue);
    }

    onMinuteBlur(): void {
        const control = this.reminderForm.get('minute');
        if (control) {
            const numValue = Math.min(Math.max(parseInt(control.value || '0', 10), 0), 59);
            control.setValue(numValue);
            this.addMinuteOptionIfNeeded(numValue);
        }
    }

    private addMinuteOptionIfNeeded(value: number): void {
        if (!this.minuteOptions.some(opt => opt.value === value)) {
            this.minuteOptions = [
                ...this.minuteOptions,
                { label: value.toString().padStart(2, '0'), value }
            ].sort((a, b) => a.value - b.value);
        }
    }

    ngOnDestroy(): void {
        if (this.ngUnsubscribe$) {
            this.ngUnsubscribe$.next();
            this.ngUnsubscribe$.complete();
            this.ngUnsubscribe$.unsubscribe();
        }
    }
}